import React from 'react';
import { EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { type BaseRecord } from '@refinedev/core';
import { ActionButton } from './ActionButton';
import { ActionButtonProps } from './ActionButton/types';
import { SmartTableColumn } from '../types';

interface ActionButtonsProps {
  actions: ActionButtonProps[];
}

interface BuildActionsColumnProps {
  showView?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  actionWidth?: number;
  customActions?: Array<{
    key: string;
    icon: React.ReactNode;
    label: string;
    onClick: (record: any) => void;
    type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
    danger?: boolean;
    tooltip?: string;
    confirmMessage?: string;
    shouldShow?: (record: any) => boolean;
  }>;
  onViewClick: (record: BaseRecord) => void;
  onEditClick: (record: BaseRecord) => void;
  onDeleteClick: (record: BaseRecord) => void;
}

export const ActionButtons: React.FC<ActionButtonsProps> = ({ actions }) => {
  return <ActionButton actions={actions} />;
};

export const buildActionsColumn = ({
  showView = false,
  showEdit = true,
  showDelete = true,
  actionWidth = 100,
  customActions = [],
  onViewClick,
  onEditClick,
  onDeleteClick,
}: BuildActionsColumnProps): SmartTableColumn => {
  const actions: any[] = [];

  // Add custom actions FIRST (before default actions)
  customActions.forEach(action => {
    actions.push({
      ...action,
      onClick: (record: BaseRecord) => action.onClick(record),
      shouldShow: action.shouldShow, // Preserve shouldShow function
    });
  });

  if (showView) {
    actions.push({
      key: 'view',
      icon: <EyeOutlined />,
      label: '',
      onClick: (record: BaseRecord) => onViewClick(record),
      tooltip: 'View details',
    });
  }

  if (showEdit) {
    actions.push({
      key: 'edit',
      icon: <EditOutlined />,
      label: '',
      onClick: (record: BaseRecord) => onEditClick(record),
      tooltip: 'Edit',
    });
  }

  if (showDelete) {
    actions.push({
      key: 'delete',
      icon: <DeleteOutlined />,
      label: '',
      onClick: (record: BaseRecord) => onDeleteClick(record),
      danger: true,
      tooltip: 'Delete',
    });
  }

  return {
    title: 'Actions',
    dataIndex: 'actions',
    key: 'actions',
    fixed: 'right' as const,
    width: actionWidth,
    render: (_: any, record: BaseRecord) => {
      // Filter actions based on shouldShow function if provided
      const filteredActions = actions.filter(action => {
        if (action.shouldShow) {
          return action.shouldShow(record);
        }
        return true;
      });

      return (
        <ActionButton
          actions={filteredActions.map(action => ({
            ...action,
            onClick: () => action.onClick(record),
          }))}
        />
      );
    },
  };
};
